import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useLanguage } from '../../features/language/useLanguage';

const buildForgotSchema = (t: (key: string, params?: Record<string, string | number>, fallback?: string) => string) =>
  z.object({
    email: z.string().email(t('auth.validation.invalidEmailSimple')),
  });

type ForgotFormData = z.infer<ReturnType<typeof buildForgotSchema>>;

export const ForgotPasswordPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const { t } = useLanguage();
  const forgotSchema = useMemo(() => buildForgotSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotFormData) => {
    try {
      await authService.forgotPassword(data.email);
    } finally {
      setSubmittedEmail(data.email);
      setSubmitted(true);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      {submitted && (
        <div
          className="mb-6 p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm"
          data-test-id="forgot-password-success"
        >
          {t('auth.forgotPassword.success', { email: submittedEmail })}
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('auth.forgotPassword.emailAddress')}
          </label>
          <div className="mt-1">
            <input
              id="email"
              type="email"
              autoComplete="email"
              data-test-id="forgot-password-email-input"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            data-test-id="forgot-password-submit-button"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        {t('auth.forgotPassword.rememberedPassword')}{' '}
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500" data-test-id="forgot-password-login-link">
          {t('auth.forgotPassword.signIn')}
        </Link>
      </div>
    </>
  );
};
